import React from 'react';

import Box from '@amzn/meridian/box';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import { type PlacementDetailsAllocatedJobShift } from 'src/store/placementDetailsPageSlice/types';
import { useAppSelector } from 'src/store/store';
import { removeSpecialCharactersFromString } from 'src/utils/helpers';
import { allocatedJobShiftsToModificationDisplayList } from 'src/utils/placementDetailUtils';

import './placementSelections.scss';

// **** Components **** //

/**
 * Default component.
 */
const PlacementSelections = () => {
  const { featureFlags } = useAppSelector((state) => state.user);
  const { placementDetails, placementSite } = useAppSelector((state) => state.placementDetailsPage);
  const { jobsDisplayed, allocatedJobShifts } = placementDetails;
  const jobModificationsEnabled = featureFlags.JOB_MODIFICATIONS && !!placementSite?.jobModificationsEnabled;
  const isT2ExperienceEnabled =
    featureFlags.T2_PLUS_EXPERIENCE && placementDetails.allocatedJobShifts?.some((j) => j.isTierTwoPlusJobModified);

  const getModificationList = (jobShift: PlacementDetailsAllocatedJobShift) => {
    if (isT2ExperienceEnabled) {
      return [];
    }

    if (jobModificationsEnabled) {
      return allocatedJobShiftsToModificationDisplayList(jobShift);
    }

    return [];
  };

  const getModificationCell = (jobShift: PlacementDetailsAllocatedJobShift) => {
    if (isT2ExperienceEnabled) {
      return jobShift.modificationNotes;
    }

    if (jobModificationsEnabled) {
      const modificationNames = jobShift.appliedModifications.map((am) => am.modificationName);
      const dedupedModNames = new Set(modificationNames);
      const modificationCellDisplay =
        dedupedModNames.size > 1 ? dedupedModNames.size + ' modifications' : modificationNames[0];

      return modificationCellDisplay;
    }

    return '';
  };

  return (
    <Table
      headerRows={1}
      rowComponents={[UncontrolledExpandableRow, TableRow]}
      showDividers={true}
      showExpandedDividers={true}
    >
      {/* Table Header */}
      <TableRow>
        <TableCell>Job(s)</TableCell>
        {(jobModificationsEnabled || isT2ExperienceEnabled) && <TableCell width="20%">Modification(s)</TableCell>}
        <TableCell>Shift</TableCell>
        <TableCell>Job Type</TableCell>
        <TableCell>Hours Assigned</TableCell>
      </TableRow>

      {/* Table Body */}
      {jobsDisplayed &&
        allocatedJobShifts?.map((jobShift, i) => {
          const jobName = removeSpecialCharactersFromString(jobShift.jobName);

          return (
            <UncontrolledExpandableRow key={i} data-cy="placement-details__job-row">
              <TableCell data-cy={`placement-details__placements__job-${jobName}`}>
                {jobShift.jobName} {placementDetails.jobSuffix}
              </TableCell>
              {(jobModificationsEnabled || isT2ExperienceEnabled) && (
                <TableCell>{getModificationCell(jobShift)}</TableCell>
              )}
              <TableCell>{jobShift.shiftCode}</TableCell>
              <TableCell>{jobShift.jobType}</TableCell>
              <TableCell data-cy={`placement-details__placements__hours-${jobName}`}>
                {jobShift.allocatedHours}
              </TableCell>

              {getModificationList(jobShift).map((modItem, j) => (
                <TableRow key={`${i}-${j}`} aria-child-row>
                  <TableCell />
                  <TableCell>
                    <Box>
                      <Text tag="div">{modItem.modificationText}</Text>
                    </Box>
                    {modItem.taskNames.map((taskName, k) => (
                      <Box key={`${i}-${j}-${k}`}>
                        <Text tag="div" type="b200" color="secondary">
                          {taskName}
                        </Text>
                      </Box>
                    ))}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>
              ))}
            </UncontrolledExpandableRow>
          );
        })}
    </Table>
  );
};

// **** Export **** //

export default PlacementSelections;
